import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import mermaid from "mermaid";
import { ZoomIn, ZoomOut, Maximize2, Expand } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

mermaid.initialize({
  startOnLoad: false,
  theme: "base",
  securityLevel: "loose",
  themeVariables: {
    background: "transparent",
    primaryColor: "#222222",
    primaryBorderColor: "#ACD6BA",
    primaryTextColor: "#FFFFFF",
    secondaryColor: "#344138",
    tertiaryColor: "#111111",
    lineColor: "#ACD6BA",
    textColor: "#FFFFFF",
    mainBkg: "#222222",
    nodeBorder: "#ACD6BA",
    clusterBkg: "#111111",
    clusterBorder: "#344138",
    edgeLabelBackground: "#000000",
    fontFamily: "Roboto, sans-serif",
  },
});

let counter = 0;

const FIT_PADDING = 16;
const CONTENT_PAD = 12;
const MIN_ZOOM = 0.25;
const MAX_ZOOM = 4;

/** Strip Mermaid defaults that break measurement (width:100%, max-width, inline style). */
function prepareSvgElement(svgEl) {
  svgEl.removeAttribute("style");
  svgEl.removeAttribute("width");
  svgEl.removeAttribute("height");
  svgEl.style.display = "block";
  svgEl.style.maxWidth = "none";
  svgEl.style.maxHeight = "none";
}

/** Actual drawn bounds — measured once; never re-read after layout (clicks must not resize). */
function measureContentBox(svgEl) {
  prepareSvgElement(svgEl);
  const box = svgEl.getBBox();
  return {
    x: box.x - CONTENT_PAD,
    y: box.y - CONTENT_PAD,
    width: box.width + CONTENT_PAD * 2,
    height: box.height + CONTENT_PAD * 2,
  };
}

function applyFitSize(svgEl, content, viewport, fitScale) {
  svgEl.setAttribute(
    "viewBox",
    `${content.x} ${content.y} ${content.width} ${content.height}`,
  );
  svgEl.setAttribute("width", String(content.width * fitScale));
  svgEl.setAttribute("height", String(content.height * fitScale));
}

const clampMul = (m) => Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, m));

function MermaidControls({ onZoomIn, onZoomOut, onReset }) {
  return (
    <div className="flex gap-1">
      <button
        type="button"
        onClick={onZoomIn}
        className="p-1.5 hover:bg-pillet-forest transition-colors"
        data-testid="mermaid-zoom-in"
        title="Zoom avant"
      >
        <ZoomIn size={16} className="text-pillet-sage" />
      </button>
      <button
        type="button"
        onClick={onZoomOut}
        className="p-1.5 hover:bg-pillet-forest transition-colors"
        data-testid="mermaid-zoom-out"
        title="Zoom arrière"
      >
        <ZoomOut size={16} className="text-pillet-sage" />
      </button>
      <button
        type="button"
        onClick={onReset}
        className="p-1.5 hover:bg-pillet-forest transition-colors"
        data-testid="mermaid-reset"
        title="Réinitialiser"
      >
        <Maximize2 size={16} className="text-pillet-sage" />
      </button>
    </div>
  );
}

const MermaidViewport = ({
  svg,
  interactive = false,
  className = "",
  style,
  onOpenModal,
}) => {
  const [zoomMul, setZoomMul] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const contentRef = useRef(null);
  const viewportRef = useRef(null);
  const svgWrapRef = useRef(null);
  const dragging = useRef(false);
  const dragOrigin = useRef({ x: 0, y: 0 });
  const panOrigin = useRef({ x: 0, y: 0 });

  const fitToViewport = useCallback(() => {
    const viewport = viewportRef.current;
    const svgEl = svgWrapRef.current?.querySelector("svg");
    if (!viewport || !svgEl || !svg) return;

    if (!contentRef.current) {
      contentRef.current = measureContentBox(svgEl);
    }

    const content = contentRef.current;
    const availW = Math.max(viewport.clientWidth - FIT_PADDING * 2, 1);
    const availH = Math.max(viewport.clientHeight - FIT_PADDING * 2, 1);
    const fitScale = Math.min(availW / content.width, availH / content.height);
    applyFitSize(svgEl, content, viewport, fitScale);
  }, [svg]);

  useLayoutEffect(() => {
    if (!svg) return;
    contentRef.current = null;
    setZoomMul(1);
    setPan({ x: 0, y: 0 });
    fitToViewport();
    const raf = requestAnimationFrame(fitToViewport);
    return () => cancelAnimationFrame(raf);
  }, [svg, fitToViewport]);

  useEffect(() => {
    if (!svg || !viewportRef.current) return;
    const ro = new ResizeObserver(() => fitToViewport());
    ro.observe(viewportRef.current);
    return () => ro.disconnect();
  }, [svg, fitToViewport]);

  const onWheel = interactive
    ? (e) => {
        e.preventDefault();
        setZoomMul((m) => clampMul(m - e.deltaY * 0.001));
      }
    : undefined;

  const onDown = interactive
    ? (e) => {
        if (e.button !== 0) return;
        e.preventDefault();
        dragging.current = true;
        dragOrigin.current = { x: e.clientX, y: e.clientY };
        panOrigin.current = { ...pan };
      }
    : undefined;

  const onMove = interactive
    ? (e) => {
        if (!dragging.current) return;
        e.preventDefault();
        setPan({
          x: panOrigin.current.x + (e.clientX - dragOrigin.current.x),
          y: panOrigin.current.y + (e.clientY - dragOrigin.current.y),
        });
      }
    : undefined;

  const onUp = interactive
    ? () => {
        dragging.current = false;
      }
    : undefined;

  const handleClick = !interactive && onOpenModal
    ? (e) => {
        if (e.target.closest("button")) return;
        onOpenModal();
      }
    : undefined;

  const reset = () => {
    setZoomMul(1);
    setPan({ x: 0, y: 0 });
  };

  const wrapTransform = interactive
    ? `translate(${pan.x}px, ${pan.y}px) scale(${zoomMul})`
    : undefined;

  return (
    <div
      ref={viewportRef}
      className={`mermaid-viewport relative w-full overflow-hidden select-none bg-black/30 flex items-center justify-center ${
        interactive ? "mermaid-viewport--interactive" : ""
      } ${!interactive && onOpenModal ? "cursor-pointer" : ""} ${className}`}
      style={style}
      onWheel={onWheel}
      onMouseDown={onDown}
      onMouseMove={onMove}
      onMouseUp={onUp}
      onMouseLeave={onUp}
      onClick={handleClick}
      role={!interactive && onOpenModal ? "button" : undefined}
      tabIndex={!interactive && onOpenModal ? 0 : undefined}
      onKeyDown={
        !interactive && onOpenModal
          ? (e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                onOpenModal();
              }
            }
          : undefined
      }
      aria-label={!interactive && onOpenModal ? "Ouvrir le schéma en grand" : undefined}
    >
      <div
        ref={svgWrapRef}
        className="mermaid-svg-wrap"
        style={{
          transform: wrapTransform,
          transformOrigin: "center center",
          transition: dragging.current ? "none" : "transform 0.15s ease",
          pointerEvents: interactive ? "auto" : "none",
        }}
        dangerouslySetInnerHTML={{ __html: svg }}
      />
      {!interactive && onOpenModal && (
        <div className="absolute inset-0 flex items-end justify-center pb-4 pointer-events-none">
          <span className="bg-black/60 text-pillet-sage text-xs font-heading uppercase tracking-widest px-3 py-1.5 border border-pillet-forest/60">
            Cliquer pour agrandir
          </span>
        </div>
      )}
      {interactive && (
        <div className="absolute top-2 right-2 z-10">
          <MermaidControls
            onZoomIn={() => setZoomMul((m) => clampMul(m + 0.2))}
            onZoomOut={() => setZoomMul((m) => clampMul(m - 0.2))}
            onReset={reset}
          />
        </div>
      )}
    </div>
  );
};

const Mermaid = ({ code }) => {
  const [svg, setSvg] = useState("");
  const [error, setError] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const idRef = useRef(`mmd-${counter++}`);

  useEffect(() => {
    let cancelled = false;
    mermaid
      .render(idRef.current, code)
      .then(({ svg: rendered }) => {
        if (!cancelled) setSvg(rendered);
      })
      .catch(() => {
        if (!cancelled) setError(true);
      });
    return () => {
      cancelled = true;
    };
  }, [code]);

  if (error) {
    return (
      <pre className="pillet-card p-4 my-4 overflow-x-auto text-pillet-muted text-sm font-mono">
        {code}
      </pre>
    );
  }

  return (
    <>
      <div
        className="pillet-card pillet-hatch my-6 relative overflow-hidden"
        data-testid="mermaid-diagram"
      >
        <div className="flex items-center justify-between px-4 py-2 border-b border-pillet-forest">
          <span className="font-heading text-xs uppercase tracking-widest text-pillet-sage">
            Schéma interactif
          </span>
          <button
            type="button"
            onClick={() => setModalOpen(true)}
            className="flex items-center gap-1.5 px-2 py-1 hover:bg-pillet-forest transition-colors text-pillet-sage"
            data-testid="mermaid-expand"
            title="Voir en grand"
          >
            <Expand size={16} />
            <span className="font-heading text-xs uppercase tracking-widest hidden sm:inline">
              Voir en grand
            </span>
          </button>
        </div>
        <MermaidViewport
          svg={svg}
          interactive={false}
          onOpenModal={() => setModalOpen(true)}
          style={{ aspectRatio: "4 / 3" }}
        />
      </div>

      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent
          className="max-w-[min(96vw,1400px)] w-[96vw] h-[min(88vh,900px)] p-0 gap-0 flex flex-col bg-pillet-card border-pillet-forest text-white overflow-hidden [&>button]:text-pillet-sage [&>button]:hover:text-white"
          data-testid="mermaid-modal"
        >
          <DialogHeader className="px-4 py-3 border-b border-pillet-forest shrink-0">
            <DialogTitle className="font-heading text-sm uppercase tracking-widest text-pillet-sage">
              Schéma interactif
            </DialogTitle>
          </DialogHeader>
          <div className="flex-1 min-h-0 p-2">
            <MermaidViewport
              key={modalOpen ? "open" : "closed"}
              svg={svg}
              interactive
              className="h-full rounded-sm"
            />
          </div>
          <p className="shrink-0 px-4 py-2 border-t border-pillet-forest text-xs text-pillet-muted">
            Molette pour zoomer · glisser pour déplacer
          </p>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default Mermaid;
